import React, { useMemo } from 'react';
import { Box, Checkbox, FieldTypes, Inscription, Option, OptionKey, SelectDropdown } from '@common-fe/common-fe';

import { REQUIRED_TEXT } from '@/common/constants';
import { RequiredLabel } from '@/components';
import { IBillingMethodTypePropertyDtoBillingMethodTypeEnum } from '@/types';

interface BaseProps {
  title: string;
  description: string;
  testId: string;
  methodType: IBillingMethodTypePropertyDtoBillingMethodTypeEnum;
  hasError?: boolean;
  isRequired?: boolean;
}

interface DropdownProps extends BaseProps {
  fieldType: FieldTypes.Dropdown;
  options: Option[];
  placeholder: string;
  value: OptionKey;
  onChange: (value: OptionKey) => void;
}

interface CheckboxProps extends BaseProps {
  fieldType: FieldTypes.Checkbox;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  disabledText?: string;
}

type Props = DropdownProps | CheckboxProps;

export const EmployerReactivationAccountUsageField: React.FC<Props> = (props) => {
  const { title, description, testId, fieldType, methodType, hasError, isRequired } = props;

  const currentMethod = useMemo(() => {
    return methodType === IBillingMethodTypePropertyDtoBillingMethodTypeEnum.PULL_FUNDS ? 'Pull funds' : 'Push funds';
  }, [methodType]);

  return (
    <Box direction="row" justify="between" data-testid={testId}>
      <Box width="244px">
        <RequiredLabel title={title} isBold isRequired={isRequired} testId={`${testId}_label`}/>

        <Inscription
          size='small'
          color="textSecondary"
          lineHeight='18px'
        >
          {description}
        </Inscription>
      </Box>

      <Box width="320px">
        {fieldType === FieldTypes.Dropdown && (
          <>
            <SelectDropdown
              id={testId}
              testId={testId}
              name={testId}
              placeholder={props.placeholder}
              singleMode
              options={props.options}
              value={props.value}
              onChange={props.onChange}
              errorText={hasError ? REQUIRED_TEXT : undefined}
            />

            <Box direction="row" margin={{ top: 'spacing8', bottom: 'spacing2'}}>
              <Inscription weight="bold" margin={{ right: 'spacing4' }}>
                Method:
              </Inscription>

              <Inscription>
                {currentMethod}
              </Inscription>
            </Box>

            {methodType === IBillingMethodTypePropertyDtoBillingMethodTypeEnum.PUSH_FUNDS && (
              <Inscription lineHeight='20px'>
                Used solely to return credits or refunds. This account will not be debited.
              </Inscription>
            )}
          </>
        )}

        {fieldType === FieldTypes.Checkbox && (
          <>
            <Checkbox
              testId={testId}
              checked={props.checked}
              onChange={props.onChange}
              disabled={props.disabled}
            />
            {props.disabled && props.disabledText && (
              <Inscription
                size="small"
                color="textSecondary"
                margin={{ top: 'spacing8' }}
                data-testid={`${testId}_disabled_text`}
              >
                {props.disabledText}
              </Inscription>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
